import { FC, Suspense, lazy, useEffect, useMemo, useState } from 'react';

import { AdminBugoFeventListModal } from './AdminBugoFeventListModal';
import {
  BugoRegionFuneralHomeRow,
  BugoRegionResult,
  BugoRegionSidoRow,
  BugoRegionSigunguRow,
} from './adminBugoRegion.types';
import { aggregateBugoRegion, feventsOfFuneralHome } from './adminBugoRegion.util';
import { exportBugoRegionExcel } from './adminBugoRegionExcel';
import { BugoStatFevent, BugoStatFilters } from './adminBugoStat.types';
import { drillDownFevents, formatRate } from './adminBugoStat.util';
import { DateRange } from './AnalyticsDateFilter';
import {
  CenterLoading,
  ErrorBox,
  NoticeBox,
  StatCard,
  TD_CLASS,
  TH_CLASS,
} from './AnalyticsShared';
import { sidoLabelOf } from './koreaMap/koreaRegion';
import { useAdminBugoRegionStat } from './useAdminBugoRegionStat';
import { useAnalyticsBrandNames } from './useAnalyticsBrandNames';

/**
 * 부고 탭 하단 "지역별 통계" — 장례식장 주소로 시도 → 시군구 → 장례식장 순으로 파고든다.
 *
 * - 데이터는 전용 집계 API(`useAdminBugoRegionStat`)에서 접힌 행으로 받고,
 *   상조·조의글·공유 필터는 부고 탭 필터를 그대로 받아 프론트에서 적용한다(재조회 없음).
 * - 지도 도형(270KB)은 `React.lazy`로만 불러 관리자 통계를 열지 않는 번들에 실리지 않게 한다.
 * - 섹션은 접힌 채 시작한다 — 열기 전엔 요청이 나가지 않고(진입 부하), 한 번 열면 세션 동안
 *   기억한다(탭 왕복 때 다시 안 눌러도 됨). 구매율 뷰와 같은 규칙.
 * - 장례식장 드릴다운은 부고 탭이 이미 받아 둔 원자료에서 고른다(서버 행엔 상가 목록이 없다).
 */

const KoreaSidoMapLazy = lazy(() =>
  import('./koreaMap/KoreaMaps').then((m) => ({ default: m.KoreaSidoMap })),
);
const KoreaSigunguMapLazy = lazy(() =>
  import('./koreaMap/KoreaMaps').then((m) => ({ default: m.KoreaSigunguMap })),
);

//* 세션 동안 기억 — 탭을 오갈 때마다 다시 펼치지 않아도 된다
let rememberedOpen = false;

/** 테스트에서 "접힌 채 시작" 상태로 되돌린다 */
export const resetBugoRegionSectionMemory = (): void => {
  rememberedOpen = false;
};

const FH_PAGE = 20;

const MapFallback: FC = () => (
  <div className="flex min-h-[260px] items-center justify-center rounded-lg bg-gray-50 text-13 text-gray-400">
    지도를 불러오는 중…
  </div>
);

/** 표 안의 비중 막대 — 숫자만으로는 상위/하위 차이가 안 보인다 */
const ShareBar: FC<{ value: number; max: number }> = ({ value, max }) => (
  <div className="flex items-center gap-1.5">
    <div className="h-2 w-16 shrink-0 overflow-hidden rounded-sm bg-gray-100">
      <div
        className="h-full rounded-sm bg-indigo-500"
        style={{ width: max > 0 ? `${Math.max(2, (value / max) * 100)}%` : '0%' }}
      />
    </div>
  </div>
);

const SectionTitle: FC<{ title: string; hint?: string; right?: React.ReactNode }> = ({
  title,
  hint,
  right,
}) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
    <h3 className="text-16 font-semibold text-gray-800">{title}</h3>
    {hint && <span className="text-13 text-gray-500">{hint}</span>}
    {right && <div className="ml-auto flex items-center gap-1.5">{right}</div>}
  </div>
);

export const AdminBugoRegionSection: FC<{
  range: DateRange;
  filters: BugoStatFilters;
  /** 드릴다운용 원자료 (부고 탭 조회 결과). 아직 없으면 목록 열기가 비활성 */
  fevents: BugoStatFevent[];
  /** 원자료가 상한에 걸려 잘렸는지 — 드릴다운 목록이 불완전할 수 있다 */
  rawTruncated: boolean;
  bugoDataUnavailable: boolean;
  acqMsgDataUnavailable: boolean;
}> = function AdminBugoRegionSection({
  range,
  filters,
  fevents,
  rawTruncated,
  bugoDataUnavailable,
  acqMsgDataUnavailable,
}) {
  const [open, setOpen] = useState(rememberedOpen);
  const [selectedSido, setSelectedSido] = useState<string | null>(null);
  const [selectedSigungu, setSelectedSigungu] = useState<string | null>(null);
  const [fhLimit, setFhLimit] = useState(FH_PAGE);
  const [drill, setDrill] = useState<{ key: string; title: string } | null>(null);

  const { resolveBrandName } = useAnalyticsBrandNames();
  const { status, rows, errorMessage, refetch } = useAdminBugoRegionStat(
    range.from,
    range.to,
    open,
  );

  const result: BugoRegionResult = useMemo(
    () => aggregateBugoRegion(rows, filters, range, resolveBrandName),
    [rows, filters, range, resolveBrandName],
  );

  //* 기간·필터가 바뀌어 선택한 시도가 사라지면 선택도 푼다 (보이지 않는 필터가 남지 않게)
  useEffect(() => {
    if (selectedSido === null) return;
    if (!result.sidos.some((row) => row.key === selectedSido)) {
      setSelectedSido(null);
      setSelectedSigungu(null);
    }
  }, [result.sidos, selectedSido]);
  useEffect(() => {
    if (selectedSigungu === null) return;
    const exists = result.sigungus.some(
      (row) => row.sido === selectedSido && row.sigungu === selectedSigungu,
    );
    if (!exists) setSelectedSigungu(null);
  }, [result.sigungus, selectedSido, selectedSigungu]);
  //* 범위가 바뀌면 "더 보기"도 처음부터
  useEffect(() => setFhLimit(FH_PAGE), [selectedSido, selectedSigungu, range]);

  const toggleOpen = () => {
    rememberedOpen = !open;
    setOpen(!open);
  };

  const selectSido = (key: string | null) => {
    setSelectedSido(key);
    setSelectedSigungu(null);
  };

  const sidoValues = useMemo(() => {
    const out: Record<string, number> = {};
    result.sidos.forEach((row) => {
      out[row.key] = row.feventCount;
    });
    return out;
  }, [result.sidos]);

  const sigunguRows: BugoRegionSigunguRow[] = useMemo(
    () => result.sigungus.filter((row) => row.sido === selectedSido),
    [result.sigungus, selectedSido],
  );
  const sigunguValues = useMemo(() => {
    const out: Record<string, number> = {};
    sigunguRows.forEach((row) => {
      if (row.sigungu) out[row.sigungu] = row.feventCount;
    });
    return out;
  }, [sigunguRows]);

  //* 장례식장 랭킹은 선택한 범위(전체 → 시도 → 시군구)로 좁힌다
  const scopedFuneralHomes: BugoRegionFuneralHomeRow[] = useMemo(
    () =>
      result.funeralHomes.filter(
        (row) =>
          (selectedSido === null || row.sido === selectedSido) &&
          (selectedSigungu === null || row.sigungu === selectedSigungu),
      ),
    [result.funeralHomes, selectedSido, selectedSigungu],
  );
  const scopeLabel =
    selectedSido === null
      ? '전국'
      : selectedSigungu === null
      ? sidoLabelOf(selectedSido)
      : `${sidoLabelOf(selectedSido)} ${selectedSigungu}`;
  const scopedTotal = scopedFuneralHomes.reduce((sum, row) => sum + row.feventCount, 0);

  const drillFevents = useMemo(
    () =>
      drill
        ? drillDownFevents(feventsOfFuneralHome(fevents, drill.key), filters, {})
        : [],
    [drill, fevents, filters],
  );
  const canDrill = fevents.length > 0;

  const totals = result.totals;
  const maxSido = result.sidos[0]?.feventCount ?? 0;
  const maxSigungu = sigunguRows[0]?.feventCount ?? 0;
  const maxFh = scopedFuneralHomes[0]?.feventCount ?? 0;

  return (
    <div className="space-y-3 rounded-lg bg-white p-3 shadow-smd">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-16 font-semibold text-gray-800">지역별 통계</h2>
        <span className="text-13 text-gray-500">
          장례식장 주소 기준 · 시도 → 시군구 → 장례식장
        </span>
        <button
          type="button"
          onClick={toggleOpen}
          className={`ml-auto rounded-lg px-3 py-1.5 text-13 font-medium ${
            open
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {open ? '접기' : '지역별 통계 보기'}
        </button>
      </div>

      {!open && (
        <p className="text-13 leading-5 text-gray-500">
          전국 지도와 시도·시군구 표, 장례식장 랭킹, 월별 추이를 봅니다. 상조·조의글·공유
          필터가 그대로 적용됩니다. 열 때 한 번만 조회합니다.
        </p>
      )}

      {open && status === 'loading' && <CenterLoading />}
      {open && status === 'error' && (
        <ErrorBox message={errorMessage ?? '오류가 발생했습니다.'} onRetry={refetch} />
      )}
      {open && status === 'notReady' && (
        <NoticeBox tone="warn">
          지역별 통계 API가 아직 이 서버에 배포되지 않았습니다. 백엔드 배포 후 다시
          확인해주세요.
        </NoticeBox>
      )}

      {open && status === 'success' && (
        <>
          {totals.feventCount === 0 ? (
            <NoticeBox>해당 조건의 상가가 없습니다.</NoticeBox>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCard
                  label="시도"
                  value={`${totals.sidoCount}곳`}
                  sub={`상가 ${totals.feventCount.toLocaleString()}건`}
                  accent="indigo"
                />
                <StatCard
                  label="시군구"
                  value={`${totals.sigunguCount.toLocaleString()}곳`}
                  sub="행사가 있던 시군구"
                />
                <StatCard
                  label="장례식장"
                  value={`${totals.funeralHomeCount.toLocaleString()}곳`}
                  sub={`장례식장당 ${(
                    totals.feventCount / Math.max(1, totals.funeralHomeCount)
                  ).toFixed(1)}건`}
                  accent="emerald"
                />
                <StatCard
                  label="주소 미분류"
                  value={totals.unresolvedFeventCount.toLocaleString()}
                  sub={
                    totals.unresolvedFeventCount > 0
                      ? '장례식장 주소가 없거나 형식이 달라 시도를 못 알아본 상가'
                      : '모든 상가가 시도로 분류됨'
                  }
                  accent={totals.unresolvedFeventCount > 0 ? 'orange' : 'gray'}
                />
              </div>

              {/* 시도 — 지도 + 표 */}
              <div className="grid gap-3 lg:grid-cols-5">
                <div className="lg:col-span-2">
                  <Suspense fallback={<MapFallback />}>
                    <KoreaSidoMapLazy
                      values={sidoValues}
                      selectedKey={selectedSido}
                      onSelect={selectSido}
                    />
                  </Suspense>
                </div>
                <div className="lg:col-span-3">
                  <SectionTitle
                    title="시도별"
                    hint="행을 누르면 시군구가 펼쳐집니다"
                    right={
                      selectedSido !== null && (
                        <button
                          type="button"
                          onClick={() => selectSido(null)}
                          className="rounded-lg bg-gray-100 px-2.5 py-1 text-13 font-medium text-gray-700 hover:bg-gray-200"
                        >
                          선택 해제
                        </button>
                      )
                    }
                  />
                  <div className="mt-1.5 overflow-x-auto">
                    <table className="w-full min-w-[520px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${TH_CLASS} text-left`}>시도</th>
                          <th className={TH_CLASS}>상가</th>
                          <th className={`${TH_CLASS} text-left`}>비중</th>
                          <th className={TH_CLASS}>공유</th>
                          <th className={TH_CLASS} title="공유 상가 ÷ 상가">
                            공유율
                          </th>
                          <th className={TH_CLASS}>장례식장</th>
                          <th className={TH_CLASS}>시군구</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.sidos.map((row: BugoRegionSidoRow) => {
                          const selected = row.key === selectedSido;
                          return (
                            <tr
                              key={row.key || '__unresolved__'}
                              onClick={() => selectSido(selected ? null : row.key)}
                              className={`cursor-pointer ${
                                selected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                              }`}
                              data-testid={`sido-row-${row.key}`}
                            >
                              <td className={`${TD_CLASS} text-left font-medium`}>
                                {row.label}
                              </td>
                              <td className={`${TD_CLASS} font-semibold`}>
                                {row.feventCount.toLocaleString()}
                              </td>
                              <td className={`${TD_CLASS} text-left`}>
                                <div className="flex items-center gap-1.5">
                                  <ShareBar value={row.feventCount} max={maxSido} />
                                  <span className="text-13 text-gray-600">
                                    {formatRate(row.feventCount, totals.feventCount)}
                                  </span>
                                </div>
                              </td>
                              <td className={TD_CLASS}>
                                {row.sharedFeventCount.toLocaleString()}
                              </td>
                              <td className={TD_CLASS}>
                                {formatRate(row.sharedFeventCount, row.feventCount)}
                              </td>
                              <td className={TD_CLASS}>{row.funeralHomeCount}</td>
                              <td className={TD_CLASS}>
                                {row.key ? row.sigunguCount : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* 시군구 — 선택한 시도만 */}
              {selectedSido !== null && (
                <div className="grid gap-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 lg:grid-cols-5">
                  <div className="lg:col-span-2">
                    {selectedSido ? (
                      <Suspense fallback={<MapFallback />}>
                        <KoreaSigunguMapLazy
                          sidoKey={selectedSido}
                          values={sigunguValues}
                          selectedSigungu={selectedSigungu}
                          onSelect={setSelectedSigungu}
                        />
                      </Suspense>
                    ) : (
                      <NoticeBox>
                        주소를 못 알아본 상가 묶음이라 지도가 없습니다. 아래 장례식장
                        목록에서 주소를 확인해주세요.
                      </NoticeBox>
                    )}
                  </div>
                  <div className="lg:col-span-3">
                    <SectionTitle
                      title={`${sidoLabelOf(selectedSido)} 시군구별`}
                      hint="행을 누르면 장례식장 목록이 그 시군구로 좁혀집니다"
                      right={
                        selectedSigungu !== null && (
                          <button
                            type="button"
                            onClick={() => setSelectedSigungu(null)}
                            className="rounded-lg bg-white px-2.5 py-1 text-13 font-medium text-gray-700 hover:bg-gray-100"
                          >
                            시군구 해제
                          </button>
                        )
                      }
                    />
                    <div className="mt-1.5 overflow-x-auto rounded-lg bg-white">
                      <table className="w-full min-w-[460px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className={`${TH_CLASS} text-left`}>시군구</th>
                            <th className={TH_CLASS}>상가</th>
                            <th className={`${TH_CLASS} text-left`}>비중</th>
                            <th className={TH_CLASS}>공유</th>
                            <th className={TH_CLASS}>공유율</th>
                            <th className={TH_CLASS}>장례식장</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sigunguRows.map((row) => {
                            const selected = row.sigungu === selectedSigungu;
                            const sidoTotal = sidoValues[selectedSido] ?? 0;
                            return (
                              <tr
                                key={row.sigungu || '__none__'}
                                onClick={() =>
                                  setSelectedSigungu(selected ? null : row.sigungu)
                                }
                                className={`cursor-pointer ${
                                  selected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                                }`}
                                data-testid={`sigungu-row-${row.sigungu}`}
                              >
                                <td className={`${TD_CLASS} text-left font-medium`}>
                                  {row.label}
                                </td>
                                <td className={`${TD_CLASS} font-semibold`}>
                                  {row.feventCount.toLocaleString()}
                                </td>
                                <td className={`${TD_CLASS} text-left`}>
                                  <div className="flex items-center gap-1.5">
                                    <ShareBar value={row.feventCount} max={maxSigungu} />
                                    <span className="text-13 text-gray-600">
                                      {formatRate(row.feventCount, sidoTotal)}
                                    </span>
                                  </div>
                                </td>
                                <td className={TD_CLASS}>
                                  {row.sharedFeventCount.toLocaleString()}
                                </td>
                                <td className={TD_CLASS}>
                                  {formatRate(row.sharedFeventCount, row.feventCount)}
                                </td>
                                <td className={TD_CLASS}>{row.funeralHomeCount}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* 장례식장 랭킹 */}
              <div>
                <SectionTitle
                  title="장례식장 랭킹"
                  hint={`${scopeLabel} · ${scopedFuneralHomes.length.toLocaleString()}곳 · 상가 ${scopedTotal.toLocaleString()}건`}
                  right={
                    <button
                      type="button"
                      onClick={() => exportBugoRegionExcel(result, range)}
                      className="rounded-lg bg-white px-3 py-1.5 text-13 font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50"
                    >
                      엑셀 (시도·시군구·장례식장·월별)
                    </button>
                  }
                />
                {!canDrill && (
                  <p className="mt-1 text-12 text-gray-500">
                    상가 목록은 위 부고 통계 조회가 끝나면 열 수 있습니다.
                  </p>
                )}
                {canDrill && rawTruncated && (
                  <p className="mt-1 text-12 text-orange-700">
                    부고 통계 원자료가 조회 상한에 걸려 있어 장례식장별 상가 목록이 일부만
                    보일 수 있습니다.
                  </p>
                )}
                <div className="mt-1.5 overflow-x-auto">
                  <table className="w-full min-w-[720px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={TH_CLASS}>#</th>
                        <th className={`${TH_CLASS} text-left`}>장례식장</th>
                        <th className={`${TH_CLASS} text-left`}>지역</th>
                        <th className={TH_CLASS}>상가</th>
                        <th className={`${TH_CLASS} text-left`}>비중</th>
                        <th className={TH_CLASS}>공유</th>
                        <th className={TH_CLASS}>공유율</th>
                        <th className={TH_CLASS} title="이 장례식장에서 행사한 상조 수">
                          상조
                        </th>
                        <th
                          className={`${TH_CLASS} text-left`}
                          title="가장 많이 행사한 상조"
                        >
                          주 상조
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {scopedFuneralHomes.slice(0, fhLimit).map((row, index) => (
                        <tr
                          key={row.key}
                          onClick={() =>
                            canDrill && setDrill({ key: row.key, title: row.name })
                          }
                          className={canDrill ? 'cursor-pointer hover:bg-gray-50' : ''}
                          data-testid={`fh-row-${row.key}`}
                        >
                          <td className={`${TD_CLASS} text-gray-500`}>{index + 1}</td>
                          <td className={`${TD_CLASS} text-left font-medium`}>
                            <span
                              className="block max-w-[260px] truncate"
                              title={row.address}
                            >
                              {row.name}
                            </span>
                          </td>
                          <td className={`${TD_CLASS} text-left text-gray-600`}>
                            {sidoLabelOf(row.sido)}
                            {row.sigungu ? ` ${row.sigungu}` : ''}
                          </td>
                          <td className={`${TD_CLASS} font-semibold`}>
                            {row.feventCount.toLocaleString()}
                          </td>
                          <td className={`${TD_CLASS} text-left`}>
                            <div className="flex items-center gap-1.5">
                              <ShareBar value={row.feventCount} max={maxFh} />
                              <span className="text-13 text-gray-600">
                                {formatRate(row.feventCount, scopedTotal)}
                              </span>
                            </div>
                          </td>
                          <td className={TD_CLASS}>
                            {row.sharedFeventCount.toLocaleString()}
                          </td>
                          <td className={TD_CLASS}>
                            {formatRate(row.sharedFeventCount, row.feventCount)}
                          </td>
                          <td className={TD_CLASS}>{row.brandCount}</td>
                          <td className={`${TD_CLASS} text-left text-gray-600`}>
                            {row.topBrandName}
                            {row.brandCount > 1 && (
                              <span className="ml-1 text-12 text-gray-400">
                                {row.topBrandCount.toLocaleString()}건
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {scopedFuneralHomes.length > fhLimit && (
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      onClick={() => setFhLimit((prev) => prev + FH_PAGE)}
                      className="rounded-lg bg-gray-100 px-4 py-1.5 text-13 font-medium text-gray-700 hover:bg-gray-200"
                    >
                      더 보기 ({(scopedFuneralHomes.length - fhLimit).toLocaleString()}곳
                      남음)
                    </button>
                  </div>
                )}
              </div>

              {/* 월별 추이 — 두 달 이상 걸칠 때만 */}
              {result.months.length >= 2 && (
                <div>
                  <SectionTitle title="월별 추이" hint="발인월 기준 · 시도별 상가 수" />
                  <div className="mt-1.5 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${TH_CLASS} text-left`}>시도</th>
                          {result.months.map((month) => (
                            <th key={month.ym} className={TH_CLASS}>
                              {month.ym}
                            </th>
                          ))}
                          <th className={TH_CLASS}>합계</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="bg-gray-50/60 font-semibold">
                          <td className={`${TD_CLASS} text-left`}>전체</td>
                          {result.months.map((month) => (
                            <td key={month.ym} className={TD_CLASS}>
                              {month.feventCount.toLocaleString()}
                            </td>
                          ))}
                          <td className={TD_CLASS}>
                            {totals.feventCount.toLocaleString()}
                          </td>
                        </tr>
                        {result.sidos.map((row) => {
                          const rowMax = Math.max(
                            0,
                            ...result.months.map((month) => month.bySido[row.key] ?? 0),
                          );
                          return (
                            <tr key={row.key || '__unresolved__'}>
                              <td className={`${TD_CLASS} text-left`}>{row.label}</td>
                              {result.months.map((month) => {
                                const value = month.bySido[row.key] ?? 0;
                                return (
                                  <td
                                    key={month.ym}
                                    className={TD_CLASS}
                                    style={{
                                      backgroundColor:
                                        value > 0 && rowMax > 0
                                          ? `rgba(79, 70, 229, ${(
                                              0.06 +
                                              0.3 * (value / rowMax)
                                            ).toFixed(3)})`
                                          : undefined,
                                    }}
                                  >
                                    {value > 0 ? value.toLocaleString() : ''}
                                  </td>
                                );
                              })}
                              <td className={`${TD_CLASS} font-semibold`}>
                                {row.feventCount.toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <NoticeBox>
                지역은 장례식장 정보(funeralHomeInfo)의 주소 첫 두 단위로 나눕니다 —
                특별·광역시는 구/군(강남구), 도는 시/군(수원시)까지입니다.
                공유·조의글·상조 필터는 위 부고 통계와 같은 값을 씁니다. 지도 경계는
                통계청 2018년 행정구역 원자료(공공누리 제1유형)라 이후 이관된 시군구는
                이름으로 맞춰 그립니다.
              </NoticeBox>
            </>
          )}
        </>
      )}

      {drill && (
        <AdminBugoFeventListModal
          title={drill.title}
          subtitle={`${range.from} ~ ${range.to} · 장례식장별 상가`}
          fevents={drillFevents}
          bugoDataUnavailable={bugoDataUnavailable}
          acqMsgDataUnavailable={acqMsgDataUnavailable}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
